import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from '@angular/router';
import {
  Actions,
  createEffect,
  ofType
} from "@ngrx/effects";
import {
  of
} from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  tap
} from "rxjs/operators";
import { ToastAlertService } from "src/app/shared/utils/toast.service";
import { IOnGoingCommitments } from "../../domain/financial-details.model";
import { IFoundersInfo, IStartUpInfoBase, IStartUpInfoFetch } from "../../domain/startup.model";
import { AdvisoryBoardService } from "../../service/advisoryBoard.service";
import { CommitmentsService } from "../../service/commitments.service";
import { FoundersService } from "../../service/founders.service";
import { StartUpService } from "../../service/startup.service";
import * as StartUpActions from "./startup.action";
import { StartUpActionTypes } from "./startup.action";
import { httpFaultMessage } from 'src/app/shared/utils/http-fault.util';
import * as StartupDashBoardActions from "../startup-dashboard/startup.dashboard.action";

@Injectable()
export class StartUpEffect {
  /**
   * Attempt to save startup.
   */
  saveStartUpInfo$ = createEffect(() => this.actions$.pipe(
    ofType<StartUpActions.SaveStartUpInfo>(StartUpActionTypes.SaveStartUpInfo),
    exhaustMap((action: StartUpActions.SaveStartUpInfo) =>
      this.startUpService.saveStartUpInfo(action.payload).pipe(
        map((data) => data.data),
        // Fix: GetProfileCompleteness() was only ever dispatched once, on
        // page load (via ProfileService's one-time getProfileData$-triggered
        // fetch). Filling in the last required field and saving never
        // refreshed `missingFields` (shared with startup-edit-page-nav-links
        // via ProfileService's live store subscription) — the nav tab's red
        // "!"/green check and its forward-navigation block stayed frozen at
        // whatever was true on page load, even once the form was genuinely
        // complete. Dispatch it alongside SaveStartUpInfoSuccess so every
        // successful save (including the decomposed per-section auto-saves,
        // which all funnel through this one action) refreshes it.
        mergeMap((data: IStartUpInfoFetch) => [
          new StartUpActions.SaveStartUpInfoSuccess(data),
          new StartupDashBoardActions.GetProfileCompleteness(),
        ]),
        tap(() => {
          if (!action.silent) {
            this.toastAlertService.showToast("Start Up Information Updated");
          }
        }),
        catchError((err: HttpErrorResponse) => of(new StartUpActions.SaveStartUpInfoFault(err.error.message)))
      )
    )
  ));

  getStartUpInfo$ = createEffect(() => this.actions$.pipe(
    ofType<StartUpActions.GetStartUpInfo>(StartUpActionTypes.GetStartUpInfo),
    exhaustMap(() =>
      this.startUpService.getStartUpInfo().pipe(
        map((data: IStartUpInfoFetch) => new StartUpActions.GetStartUpInfoSuccess(data)),
        catchError((err: HttpErrorResponse) => of(new StartUpActions.GetStartUpInfoFault(httpFaultMessage(err))))
      )
    )
  ));

  getFounders$ = createEffect(() => this.actions$.pipe(
    ofType<StartUpActions.GetFounders>(StartUpActionTypes.GetFounders),
    exhaustMap(() =>
      this.foundersService.getFounders().pipe(
        map((data: IFoundersInfo[]) => new StartUpActions.GetFoundersSuccess(data)),
        catchError((err: HttpErrorResponse) => of(new StartUpActions.GetFoundersFault(httpFaultMessage(err))))
      )
    )
  ));


  getAdvisoryBoard$ = createEffect(() => this.actions$.pipe(
    ofType<StartUpActions.GetAdvisoryBoard>(StartUpActionTypes.GetAdvisoryBoard),
    exhaustMap(() =>
      this.advisoryBoardService.getAdvisoryBoard().pipe(
        map((data: IFoundersInfo[]) => new StartUpActions.GetAdvisoryBoardSuccess(data)),
        catchError((err: HttpErrorResponse) => of(new StartUpActions.GetAdvisoryBoardFault(httpFaultMessage(err))))
      )
    )
  ));

  getCommitments$ = createEffect(() => this.actions$.pipe(
    ofType<StartUpActions.GetCommitments>(StartUpActionTypes.GetCommitments),
    exhaustMap(() =>
      this.commitmentsService.getOnGoingCommitments().pipe(
        map((data: IOnGoingCommitments[]) => new StartUpActions.GetCommitmentsSuccess(data)),
        catchError((err: HttpErrorResponse) => of(new StartUpActions.GetCommitmentsFault(httpFaultMessage(err))))
      )
    )
  ));

  constructor(
    private actions$: Actions,
    private startUpService: StartUpService,
    private toastAlertService: ToastAlertService,
    private router: Router,
    private readonly foundersService: FoundersService,
    private readonly advisoryBoardService: AdvisoryBoardService,
    private readonly commitmentsService: CommitmentsService,
  ) { }
}
